'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { MdSearch, MdCheckCircle, MdOutlineConfirmationNumber } from 'react-icons/md';
import { getVehicleBrandIconPath } from '@/lib/vehicle-brand-utils';
import { hasSpecialRateAccess } from '@/lib/special-rate-utils';
import { buildRateRows } from '@/lib/special-rate-rows';
import { toChargeTicket } from '@/lib/scanned-ticket-adapter';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Tabs, Tab } from '@/components/ui/tab/Tab';
import { TabType, TabSize } from '@/components/ui/tab/Tab.types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { Text } from '@/components/ui/text/Text';
import { Status } from '@/components/ui/status';
import { StatusState } from '@/components/ui/status/Status.types';
import { Colors } from '@/lib/colorUtils';
import { useRouter } from '@/lib/app-router';
import { useAuthStore } from '@/stores/user.store';
import { useThemeStore } from '@/stores/theme.store';
import { useVehicleStore } from '@/stores/vehicle.store';
import { useTicketStore } from '@/stores/ticket.store';
import { useParkingValidationStore } from '@/stores/parking-validation.store';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { makeRecaptchaHeader } from '@/hooks/useRecaptcha';
import { extractApiErrorMessage } from '@/lib/api-error-utils';
import { TOUCHLESS_ROUTES, MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';
import { useTouchlessStore } from '@/stores/touchless.store';
import { useQrScanner } from '@/hooks/useQrScanner';
import { Select } from '@/components/ui/select';
import { SHEET_MENU_Z_INDEX } from '@/lib/styleUtils';
import { logger } from '@/lib/logger';
import { formatReservationTime } from '@/lib/date-time-utils';
import { differenceInMinutes, isToday } from 'date-fns';
import { DiscountProgramPickerSheet } from '@/components/myaccount/parking-validation/discount-picker';
import { ChargeToAccountModal } from '@/components/myaccount/parking-validation/charge-to-account/ChargeToAccountModal';
import {
  contentClasses,
  scanQrContainerClasses,
  scanQrViewfinderClasses,
  scanQrErrorClasses,
  vehicleSectionClasses,
  vehicleSectionHeaderClasses,
  vehicleItemClasses,
  vehicleItemLeftClasses,
  vehicleNameClasses,
  vehiclePlateClasses,
  searchingContainerClasses,
  spinnerClasses,
  resultContainerClasses,
  successIconClasses,
  resultCardClasses,
  resultCardBodyClasses,
  resultCardInfoClasses,
  resultCardGarageRowClasses,
  resultCardAmountClasses,
  noResultClasses,
} from './FindSessionModal.style';
import type {
  FindSessionModalState,
  FindSessionModalProps,
  FindSessionTab,
} from './FindSessionModal.types';

const TAB_LABEL_KEYS: Record<FindSessionTab, string> = {
  vehicle: 'tabVehicle',
  ticket: 'tabTicket',
  scanQr: 'tabScanQr',
};

function formatTicketTime(enterTimestamp?: number): string {
  if (!enterTimestamp) return '';
  const enterDate = new Date(enterTimestamp);
  const now = new Date();
  const time = formatReservationTime(enterDate);
  const totalMinutes = differenceInMinutes(now, enterDate);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const duration = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  const dayLabel = isToday(enterDate) ? 'Today' : enterDate.toLocaleDateString();
  return `${dayLabel}, ${time} · ${duration}`;
}

export const FindSessionModal = ({
  open,
  onClose,
  initialTab = 'vehicle',
  entry = 'scan',
}: FindSessionModalProps) => {
  const t = useTranslations('Landing');
  const router = useRouter();

  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const showTicketTab = hasSpecialRateAccess(user, theme);

  // Two legacy features share this modal, and the entry point decides which one:
  // the home-page bar is Pay by Plate (plate lookup, any signed-in user on an
  // enabled company — legacy HomePage.tsx:1382 checks only isAuthorized), while
  // the Scan entries are the Scan Ticket button (QR for everyone, ticket-number
  // text search for validators only — legacy QrScan.tsx:514).
  const availableTabs = useMemo<FindSessionTab[]>(() => {
    if (entry === 'payByPlate') return ['vehicle'];
    return showTicketTab ? ['scanQr', 'vehicle', 'ticket'] : ['scanQr'];
  }, [entry, showTicketTab]);

  const vehicles = useVehicleStore((s) => s.vehicles);
  const nearbyTickets = useTicketStore((s) => s.nearbyTickets);
  const searchedTicket = useTicketStore((s) => s.searchedTicket);
  const isSearching = useTicketStore((s) => s.isSearching);
  const searchTicket = useTicketStore((s) => s.searchTicket);
  const clearResults = useTicketStore((s) => s.clearResults);
  const { getToken } = useRecaptcha();

  const decodeQr = useTouchlessStore((s) => s.decodeQr);
  const getCompanyInfo = useTouchlessStore((s) => s.getCompanyInfo);

  // Validation session store (PB-2292)
  const validationSession = useParkingValidationStore((s) => s.validationSession);
  const specialRateUsage = useParkingValidationStore((s) => s.specialRateUsage);
  const searchTicketForValidation = useParkingValidationStore((s) => s.searchTicketForValidation);
  const setValidationSelectedProgram = useParkingValidationStore(
    (s) => s.setValidationSelectedProgram
  );
  const clearValidationSession = useParkingValidationStore((s) => s.clearValidationSession);

  const [activeTab, setActiveTab] = useState<FindSessionTab>(initialTab);
  const [searchText, setSearchText] = useState('');
  const [ticketSearchText, setTicketSearchText] = useState('');
  const [modalState, setModalState] = useState<FindSessionModalState>('search');
  const [searchError, setSearchError] = useState<string | null>(null);
  const lastScannedRef = useRef<string | null>(null);

  // Special-rate UI state (PB-2292)
  const [showCharge, setShowCharge] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // Derived: whether to show the special-rate result block instead of the
  // non-special-rate Continue→touchless block.
  // Only active on the ticket tab for users with special rate access.
  const showValidateResult =
    showTicketTab &&
    (activeTab === 'ticket' || activeTab === 'vehicle') &&
    modalState === 'result' &&
    !!validationSession.ticket;
  const chargeEnabled = validationSession.chargeToAccountEnabled;

  // The two searches land in different stores — ticket-number search writes to
  // ticket.store, plate search to validationSession — so the plain result card
  // and Continue have to read from whichever ran. TicketSearchResult extends
  // DBTicket, so both expose the same display fields and `id`.
  const resultTicket = searchedTicket ?? validationSession.ticket;

  // Build rate rows from the validation session ticket for the Validate flow.
  const rateRows = useMemo(
    () => buildRateRows(validationSession.ticket ?? null, undefined, specialRateUsage),
    [validationSession.ticket, specialRateUsage]
  );

  /**
   * Routes a scanned QR code to the correct flow.
   *
   * ## Invariants:
   * - Booking URLs (/qr/, /z/) always navigate away for all users — they are reservation
   *   links, not ticket validation entries.
   * - For touchless ticket URLs and encrypted QR ticket IDs:
   *   - Special-rate users (showTicketTab): route through the validation flow so the
   *     Validate/Charge result card appears inside the modal (modal stays open).
   *   - Non-special-rate users: navigate to the touchless pay screen as before.
   * - onClose() is called only when navigating away; the modal stays open when showing
   *   the validation result to a special-rate user.
   */
  const handleQrResult = useCallback(
    async (text: string) => {
      // Debounce: skip if same code scanned within short window
      if (lastScannedRef.current === text) return;
      lastScannedRef.current = text;
      setTimeout(() => {
        lastScannedRef.current = null;
      }, 3000);

      logger.info('QR code scanned', { feature: 'find-session', data: text });

      // QR booking URLs (/qr/, /z/) — reservation links, route for all users.
      if (text.includes('/qr/') || text.includes('/z/')) {
        onClose();
        try {
          const url = new URL(text, window.location.origin);
          router.push(url.pathname + url.search);
        } catch {
          router.push(text);
        }
        return;
      }

      // Helper: run the ticket validation flow for special-rate users instead of
      // navigating to touchless. Mirrors handleTicketSearch's special-rate path.
      const runValidationSearch = async (ticketId: string) => {
        setActiveTab('ticket');
        setSearchError(null);
        setModalState('searching');
        try {
          const token = await getToken('GUEST_PAYMENT');
          const headers = makeRecaptchaHeader(token);
          const result = await searchTicketForValidation(undefined, ticketId, undefined, headers);
          setModalState(result ? 'result' : 'no-result');
        } catch (err) {
          setSearchError(extractApiErrorMessage(err, t('searchError' as Parameters<typeof t>[0])));
          setModalState('no-result');
        }
      };

      // Touchless ticket URLs (myvm.us, prkng.co, /touchless/, /t/)
      const touchlessPatterns = ['myvm.us', 'prkng.co', '/touchless/', '/t/'];
      const matchedTouchless = touchlessPatterns.some((p) => text.includes(p));
      if (matchedTouchless) {
        // Extract ticket ID from the URL path
        const urlParts = text.split('/');
        const ticketId = urlParts[urlParts.length - 1];
        if (ticketId) {
          if (showTicketTab) {
            await runValidationSearch(ticketId);
          } else {
            onClose();
            router.push(TOUCHLESS_ROUTES.ticket(ticketId));
          }
          return;
        }
      }

      // Encrypted QR data — decode via backend
      try {
        const result = await decodeQr({ qrCode: text } as Parameters<typeof decodeQr>[0]);
        if (result && (result as Record<string, unknown>).ticketId) {
          const ticketId = (result as Record<string, unknown>).ticketId as string;
          if (showTicketTab) {
            await runValidationSearch(ticketId);
          } else {
            onClose();
            router.push(TOUCHLESS_ROUTES.ticket(ticketId));
          }
          return;
        }
      } catch {
        logger.warn('Failed to decode QR', { feature: 'find-session', data: text });
      }
    },
    [onClose, router, decodeQr, showTicketTab, setActiveTab, getToken, searchTicketForValidation]
  );

  const {
    videoRef,
    error: scannerError,
    cameras,
    selectedCameraId,
    selectCamera,
  } = useQrScanner(open && activeTab === 'scanQr', handleQrResult);

  const cameraOptions = cameras.map((c) => ({ value: c.deviceId, label: c.label }));

  // Reset state when modal closes; sync initialTab when modal opens.
  // setState calls inside this effect are intentional — they synchronise
  // local modal state with the external `open` prop (a controlled-component
  // pattern). The rule disables are acknowledged here; restructuring to
  // avoid effects would require lifting state out of the component.
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchText('');

      setTicketSearchText('');

      setModalState('search');

      setShowCharge(false);

      setShowPicker(false);

      setSearchError(null);
      clearResults();
      // NOTE: do NOT clearValidationSession() here. Selecting a program closes this
      // modal and navigates to the full-page validate form, which reads the ticket +
      // selectedProgram from validationSession. Clearing on close would wipe that
      // hand-off before the page mounts. The session is instead cleared on explicit
      // Cancel (handleCancel) and after Apply (useValidateParking), and is overwritten
      // by the next search.
    } else {
      // 'vehicle' (the default) is gated away for non-special-rate users; fall back so
      // the modal never opens on a hidden tab. (PB-2295)
      setActiveTab(availableTabs.includes(initialTab) ? initialTab : availableTabs[0]);
    }
  }, [open, clearResults, initialTab, availableTabs]);

  // Track previous isSearching to detect true → false transition (non-special-rate ticket/vehicle tabs)
  const wasSearchingRef = useRef(false);
  useEffect(() => {
    if (wasSearchingRef.current && !isSearching) {
      if (activeTab === 'ticket' && !showTicketTab) {
        // Non-special-rate ticket search (ticket tab not normally visible for non-special-rate users,
        // but guard here for safety)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setModalState(searchedTicket ? 'result' : 'no-result');
      } else if (activeTab !== 'ticket') {
        setModalState(nearbyTickets.length > 0 ? 'result' : 'no-result');
      }
    }
    wasSearchingRef.current = isSearching;
  }, [isSearching, nearbyTickets, searchedTicket, activeTab, showTicketTab]);

  const filteredVehicles = useMemo(() => {
    if (!searchText.trim()) return vehicles;
    const query = searchText.toLowerCase();
    return vehicles.filter(
      (v) =>
        v.vehicleInfo?.vehicleName?.toLowerCase().includes(query) ||
        v.vehicleInfo?.vehicleMake?.toLowerCase().includes(query) ||
        v.vehicleInfo?.vehicleModel?.toLowerCase().includes(query) ||
        v.vehicleInfo?.vehicleLicensePlate?.toLowerCase().includes(query)
    );
  }, [vehicles, searchText]);

  /**
   * Vehicle-tab search — by license plate (PB-2295).
   *
   * ## Invariants:
   * - Same lookup as the Ticket tab (searchTicketNew via searchTicketForValidation); only
   *   the value differs (passed as licensePlate), so both gated tabs share the result card.
   */
  const handleVehicleSearch = useCallback(async () => {
    if (!searchText.trim()) return;
    setSearchError(null);
    setModalState('searching');
    try {
      const token = await getToken('GUEST_PAYMENT');
      const headers = makeRecaptchaHeader(token);
      const result = await searchTicketForValidation(
        undefined,
        undefined,
        searchText.trim(),
        headers
      );
      setModalState(result ? 'result' : 'no-result');
    } catch (err) {
      setSearchError(extractApiErrorMessage(err, t('searchError' as Parameters<typeof t>[0])));
      setModalState('no-result');
    }
  }, [searchText, searchTicketForValidation, getToken, t]);

  /**
   * Ticket search handler.
   *
   * ## Invariants:
   * - Special-rate users (showTicketTab) route through searchTicketForValidation so the
   *   result carries specialRatePrograms / rateOutputs / chargeToAccountEnabled in
   *   validationSession. Non-special-rate users never see the ticket tab, but the guard
   *   falls back to searchTicket for safety.
   * - setModalState is called directly on completion here (not via a loading-flag
   *   effect), so the modal transitions correctly regardless of the store's loading
   *   flag shape.
   */
  const handleTicketSearch = useCallback(async () => {
    if (!ticketSearchText.trim()) return;
    setSearchError(null);
    setModalState('searching');
    try {
      const token = await getToken('GUEST_PAYMENT');
      const headers = makeRecaptchaHeader(token);
      if (showTicketTab) {
        // Special-rate path: use validation store so the result carries rate programs.
        const result = await searchTicketForValidation(
          ticketSearchText.trim(),
          undefined,
          undefined,
          headers
        );
        setModalState(result ? 'result' : 'no-result');
      } else {
        await searchTicket(ticketSearchText.trim(), undefined, headers);
        // Non-special-rate: modal state transition driven by wasSearchingRef / isSearching effect.
      }
    } catch (err) {
      // Surface the failure (e.g. "Captcha Validation Failed") instead of hanging on the loader.
      setSearchError(extractApiErrorMessage(err, t('searchError' as Parameters<typeof t>[0])));
      setModalState('no-result');
    }
  }, [ticketSearchText, searchTicket, searchTicketForValidation, showTicketTab, getToken, t]);

  const handleCancel = useCallback(() => {
    setModalState('search');
    setSearchError(null);
    clearResults();
    clearValidationSession();
  }, [clearResults, clearValidationSession]);

  const handleContinue = useCallback(() => {
    const ticketId = resultTicket?.id ?? nearbyTickets[0]?.ticketId;
    if (!ticketId) return;
    onClose();
    router.push(TOUCHLESS_ROUTES.ticket(ticketId));
  }, [resultTicket, nearbyTickets, onClose, router]);

  /**
   * Validate handler — picks the program and navigates to the validate route.
   *
   * ## Invariants:
   * - If exactly one selectable rate row exists, select it immediately and navigate.
   *   If multiple, open the picker sheet for the user to choose.
   * - setValidationSelectedProgram is called before navigation so the validate page
   *   has the program ready in the store on mount.
   */
  const handleValidate = useCallback(() => {
    if (!validationSession.ticket?.id) return;
    // Always open the "Choose discount program" popup (Figma 12509-84516) instead of
    // navigating. The picker lists every applicable rate and routes onward on select,
    // so the user never lands on an empty page when no rate is pre-selectable.
    setShowPicker(true);
  }, [validationSession.ticket]);

  const handlePickerSelect = useCallback(
    (specialRateId: string) => {
      if (!validationSession.ticket?.id) return;
      const ticketId = validationSession.ticket.id;
      const program =
        (validationSession.ticket.specialRatePrograms ?? []).find(
          (p) => p.specialRateId === specialRateId
        ) ?? null;
      setValidationSelectedProgram(program);
      setShowPicker(false);
      onClose();
      router.push(MY_ACCOUNT_ROUTES.validate(ticketId));
    },
    [validationSession.ticket, setValidationSelectedProgram, onClose, router]
  );

  // Avoid -1 before the open-effect syncs activeTab into availableTabs. (PB-2295)
  const activeTabIndex = Math.max(0, availableTabs.indexOf(activeTab));

  const handleTabChange = (index: number) => {
    setActiveTab(availableTabs[index]);
    setModalState('search');
    setSearchText('');
    setTicketSearchText('');
    setSearchError(null);
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('findParkingSession')}
      data-cy="find-session-modal"
    >
      <div className={contentClasses}>
        {/* A lone tab is chrome with no choice behind it — the Pay by Plate entry
            offers only the plate lookup, exactly as legacy's tab does. */}
        {modalState === 'search' && availableTabs.length > 1 && (
          <Tabs
            type={TabType.box}
            size={TabSize.small}
            activeIndex={activeTabIndex}
            onTabChange={handleTabChange}
            data-cy="find-session-tabs"
          >
            {/* Driven off availableTabs so the strip can't drift from it —
                activeTabIndex indexes into that array. (PB-2295, PB-4360) */}
            {availableTabs.map((tab) => (
              <Tab key={tab}>{t(TAB_LABEL_KEYS[tab] as Parameters<typeof t>[0])}</Tab>
            ))}
          </Tabs>
        )}

        {/* Scan QR tab */}
        {activeTab === 'scanQr' && (
          <div className={scanQrContainerClasses}>
            {scannerError ? (
              <div className={scanQrErrorClasses}>
                <Text
                  size="14"
                  weight="medium"
                  color={Colors.colorBrandError}
                  data-cy="find-session-scanner-error"
                >
                  {scannerError}
                </Text>
              </div>
            ) : (
              <>
                {cameraOptions.length > 1 && (
                  <Select
                    label={t('tabScanQr' as Parameters<typeof t>[0])}
                    options={cameraOptions}
                    value={selectedCameraId ?? undefined}
                    onChange={selectCamera}
                    hideBottomPadding
                    dataCy="find-session-camera-select"
                    menuZIndex={SHEET_MENU_Z_INDEX}
                  />
                )}
                <div className={scanQrViewfinderClasses}>
                  <video
                    ref={videoRef}
                    className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover"
                    muted
                    playsInline
                  />
                  {/* Corner brackets */}
                  <svg
                    className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:pointer-events-none"
                    viewBox="0 0 200 200"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    {/* Top-left */}
                    <path
                      d="M10 40 L10 10 L40 10"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    {/* Top-right */}
                    <path
                      d="M160 10 L190 10 L190 40"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    {/* Bottom-left */}
                    <path
                      d="M10 160 L10 190 L40 190"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    {/* Bottom-right */}
                    <path
                      d="M160 190 L190 190 L190 160"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {/* Scanning line animation */}
                  <div className="tw:absolute tw:left-[5%] tw:w-[90%] tw:h-[3px] tw:bg-primary tw:rounded-full tw:opacity-80 tw:animate-[scanLine_2.5s_ease-in-out_infinite] tw:shadow-[0_0_8px_2px_rgba(44,80,130,0.5)]" />
                </div>
              </>
            )}
          </div>
        )}

        {/* Vehicle tab — search by license plate (PB-2295) */}
        {activeTab === 'vehicle' && modalState === 'search' && (
          <form
            className="tw:flex tw:flex-col tw:gap-base"
            onSubmit={(e) => {
              e.preventDefault();
              handleVehicleSearch();
            }}
          >
            <Input
              value={searchText}
              onChange={(value) => setSearchText(value)}
              placeholder={t('searchByVehicle')}
              leftIcon={<MdSearch size={20} aria-hidden="true" />}
              data-cy="find-session-search-input"
            />
            <Button
              type="submit"
              size={ButtonSize.large}
              variant={ButtonVariant.primary}
              disabled={!searchText.trim()}
              className="tw:w-full"
              data-cy="find-session-vehicle-search"
            >
              {t('search' as Parameters<typeof t>[0])}
            </Button>

            {filteredVehicles.length > 0 && (
              <div className={vehicleSectionClasses}>
                <div className={vehicleSectionHeaderClasses}>{t('orChooseVehicle')}</div>
                {filteredVehicles.map((vehicle) => {
                  const name =
                    vehicle.vehicleInfo?.vehicleName ||
                    [vehicle.vehicleInfo?.vehicleMake, vehicle.vehicleInfo?.vehicleModel]
                      .filter(Boolean)
                      .join(' ');
                  const plate = vehicle.vehicleInfo?.vehicleLicensePlate || '';
                  return (
                    <button
                      key={vehicle.documentId}
                      type="button"
                      className={vehicleItemClasses}
                      onClick={() => setSearchText(plate)}
                      data-cy="find-session-vehicle"
                    >
                      <div className={vehicleItemLeftClasses}>
                        <img
                          src={getVehicleBrandIconPath(vehicle.vehicleInfo?.vehicleMake)}
                          alt=""
                          aria-hidden="true"
                          className="tw:w-5 tw:h-5 tw:object-contain tw:shrink-0"
                        />
                        <span className={vehicleNameClasses}>{name}</span>
                      </div>
                      <span className={vehiclePlateClasses}>{plate}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </form>
        )}

        {/* Ticket tab content — search by ticket number */}
        {activeTab === 'ticket' && modalState === 'search' && (
          <form
            className="tw:flex tw:flex-col tw:gap-base"
            onSubmit={(e) => {
              e.preventDefault();
              handleTicketSearch();
            }}
          >
            <Input
              value={ticketSearchText}
              onChange={(value) => setTicketSearchText(value)}
              placeholder={t('ticketNumberPlaceholder' as Parameters<typeof t>[0])}
              leftIcon={<MdOutlineConfirmationNumber size={20} aria-hidden="true" />}
              data-cy="find-session-ticket-input"
            />
            <Button
              type="submit"
              size={ButtonSize.large}
              variant={ButtonVariant.primary}
              disabled={!ticketSearchText.trim()}
              className="tw:w-full"
              data-cy="find-session-ticket-search"
            >
              {t('search' as Parameters<typeof t>[0])}
            </Button>
          </form>
        )}

        {(activeTab === 'vehicle' || activeTab === 'ticket') && modalState === 'searching' && (
          <div className={searchingContainerClasses}>
            <div className={spinnerClasses} role="status" aria-busy="true" aria-label="Loading" />
            <Text size="16" weight="medium" color={Colors.colorFont}>
              {t('searching')}
            </Text>
            <Button
              size={ButtonSize.large}
              variant={ButtonVariant.secondary}
              onClick={handleCancel}
              data-cy="find-session-cancel"
            >
              {t('cancel')}
            </Button>
          </div>
        )}

        {/* Validate/Charge result — shared by both gated tabs (PB-2295) */}
        {showValidateResult && validationSession.ticket && (
          <div className={resultContainerClasses}>
            <div className={successIconClasses}>
              <MdCheckCircle size={36} color="#4caf50" aria-hidden="true" />
            </div>
            <Text size="20" weight="bold" color={Colors.colorFont}>
              {t('parkingFound')}
            </Text>

            <div className={resultCardClasses} data-cy="find-session-ticket-result-card">
              <div className={resultCardBodyClasses}>
                <div className={resultCardInfoClasses}>
                  <div className={resultCardGarageRowClasses}>
                    <Text
                      size="16"
                      weight="bold"
                      color={Colors.colorFont}
                      data-cy="find-session-validate-ticket-number"
                    >
                      {validationSession.ticket.ticket ||
                        `#${validationSession.ticket.ticketNumber}`}
                    </Text>
                    <Status
                      status={StatusState.positive}
                      text={t('active' as Parameters<typeof t>[0])}
                    />
                  </div>
                  <Text size="14" weight="regular" color={Colors.colorGrayDarker}>
                    {[
                      validationSession.ticket.make,
                      validationSession.ticket.color,
                      validationSession.ticket.plate,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  <Text size="14" weight="regular" color={Colors.colorGrayDarker}>
                    {formatTicketTime(validationSession.ticket.enter)}
                  </Text>
                </div>
                {typeof validationSession.ticket.total === 'number' &&
                  validationSession.ticket.total > 0 && (
                    <div className={resultCardAmountClasses}>
                      <Text size="12" weight="regular" color={Colors.colorGrayDarker}>
                        {t('toPay' as Parameters<typeof t>[0])}
                      </Text>
                      <Text
                        size="20"
                        weight="bold"
                        color={Colors.colorFont}
                        data-cy="find-session-validate-total-amount"
                      >
                        ${validationSession.ticket.total.toFixed(2)}
                      </Text>
                    </div>
                  )}
              </div>
            </div>

            <div className="tw:flex tw:gap-base tw:w-full">
              <Button
                size={ButtonSize.large}
                variant={ButtonVariant.primary}
                className="tw:flex-1"
                onClick={handleValidate}
                data-cy="find-session-validate"
              >
                {t('validate' as Parameters<typeof t>[0])}
              </Button>
              {chargeEnabled && (
                <Button
                  size={ButtonSize.large}
                  variant={ButtonVariant.secondary}
                  className="tw:flex-1"
                  onClick={() => setShowCharge(true)}
                  data-cy="find-session-charge"
                >
                  {t('chargeToAccount' as Parameters<typeof t>[0])}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Plain result — Continue → touchless. Covers the plate tab too, which a
            non-validator reaches from the Pay by Plate bar. */}
        {(activeTab === 'ticket' || activeTab === 'vehicle') &&
          modalState === 'result' &&
          !showValidateResult &&
          resultTicket && (
            <div className={resultContainerClasses}>
              <div className={successIconClasses}>
                <MdCheckCircle size={36} color="#4caf50" aria-hidden="true" />
              </div>
              <Text size="20" weight="bold" color={Colors.colorFont}>
                {t('ticketFound' as Parameters<typeof t>[0])}
              </Text>

              <div className={resultCardClasses} data-cy="find-session-ticket-result-card">
                <div className={resultCardBodyClasses}>
                  <div className={resultCardInfoClasses}>
                    <div className={resultCardGarageRowClasses}>
                      <Text
                        size="16"
                        weight="bold"
                        color={Colors.colorFont}
                        data-cy="find-session-scan-ticket-number"
                      >
                        {resultTicket.ticket || `#${resultTicket.ticketNumber}`}
                      </Text>
                      {/* searchTicketNew doesn't return a user-facing status string, so
                        there's nothing reliable to show here. The previous code read
                        DBTicket.state, which is the vehicle's license-plate state. */}
                    </div>
                    <Text size="14" weight="regular" color={Colors.colorGrayDarker}>
                      {[resultTicket.make, resultTicket.color, resultTicket.plate]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    <Text size="14" weight="regular" color={Colors.colorGrayDarker}>
                      {formatTicketTime(resultTicket.enter)}
                    </Text>
                  </div>
                  {/* `total` is the stored running total on the DBTicket — not a live rate
                    quote. Show the "To pay" block only when there's a real amount due;
                    the live rate is computed on the touchless page after Continue. */}
                  {typeof resultTicket.total === 'number' && resultTicket.total > 0 && (
                    <div className={resultCardAmountClasses}>
                      <Text size="12" weight="regular" color={Colors.colorGrayDarker}>
                        {t('toPay' as Parameters<typeof t>[0])}
                      </Text>
                      <Text
                        size="20"
                        weight="bold"
                        color={Colors.colorFont}
                        data-cy="find-session-scan-total-amount"
                      >
                        ${resultTicket.total.toFixed(2)}
                      </Text>
                    </div>
                  )}
                </div>
              </div>

              <Button
                size={ButtonSize.large}
                variant={ButtonVariant.secondary}
                onClick={handleContinue}
                className="tw:w-full"
                data-cy="find-session-ticket-continue"
              >
                {t('continue')}
              </Button>
            </div>
          )}

        {(activeTab === 'vehicle' || activeTab === 'ticket') && modalState === 'no-result' && (
          <div className={noResultClasses}>
            <Text
              size="16"
              weight="medium"
              color={searchError ? Colors.colorBrandError : Colors.colorGrayDarker}
              data-cy="find-session-no-result-message"
            >
              {searchError ||
                (activeTab === 'ticket'
                  ? t('noTicketFound' as Parameters<typeof t>[0])
                  : t('noTicketsFound'))}
            </Text>
            <Button
              size={ButtonSize.medium}
              variant={ButtonVariant.secondary}
              onClick={handleCancel}
              data-cy="find-session-back"
            >
              {t('cancel')}
            </Button>
          </div>
        )}
      </div>

      {/* Discount program picker — shown when multiple selectable programs exist.
          Elevated z-index so it stacks ABOVE this BottomSheet: its backdrop (z-[60])
          must sit over the Find-Session sheet (z-50) to dim it, and its own sheet
          (z-[70]) above that. Without this both sheets share z-40/z-50 and render
          merged with no separating backdrop. */}
      <DiscountProgramPickerSheet
        open={showPicker}
        onClose={() => setShowPicker(false)}
        rates={rateRows}
        loading={false}
        onSelect={handlePickerSelect}
        zIndex={{ backdrop: 'tw:z-[60]', sheet: 'tw:z-[70]' }}
      />

      {/* Charge to account modal — gated on chargeToAccountEnabled */}
      {validationSession.ticket && (
        <ChargeToAccountModal
          ticket={toChargeTicket(validationSession.ticket)}
          isOpen={showCharge}
          onClose={() => setShowCharge(false)}
          onSuccess={() => {
            // ChargeToAccountModal shows its own success toast on a completed charge,
            // so we just close the modals here.
            setShowCharge(false);
            onClose();
          }}
        />
      )}
    </BottomSheet>
  );
};
